import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Profile } from "@/types/profile";
import { Course } from "@/types/course";
import { ProfileHeader } from "@/components/creator-profile/ProfileHeader";
import { SocialLinks } from "@/components/creator-profile/SocialLinks";
import { CourseGrid } from "@/components/creator-profile/CourseGrid";
import { ProfileFooter } from "@/components/creator-profile/ProfileFooter";
import { ProfileError } from "@/components/creator-profile/ProfileError";
import { ProfileLoading } from "@/components/creator-profile/ProfileLoading";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import NotFound from "./NotFound";

const CreatorProfile = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();

  // Guard against reserved route names being treated as usernames
  useEffect(() => {
    if (username === "student-auth") {
      console.log("[CreatorProfile] Blocked access to reserved path 'student-auth'");
      navigate("/not-found", { replace: true });
    }
  }, [username, navigate]);

  const { 
    data: profile,
    isLoading: profileLoading,
    error: profileError
  } = useQuery({
    queryKey: ['profile', username],
    queryFn: async () => {
      console.log("[CreatorProfile] Attempting to fetch profile for username:", username);
      
      if (!username) {
        console.error("[CreatorProfile] Username parameter is empty or undefined");
        throw new Error("Username is required");
      }
      
      const result = await supabase.rpc('get_profile_by_username', {
        username_param: username
      });

      console.log("[CreatorProfile] Profile fetch result:", { 
        hasResult: !!result,
        error: result === null ? "Error fetching profile" : null
      });
      
      if (result === null) {
        console.error("[CreatorProfile] Error fetching profile");
        throw new Error("Creator lookup failed");
      }
      
      if (!result || !Array.isArray(result) || result.length === 0) {
        console.warn("[CreatorProfile] No profile found for username:", username);
        throw new Error("Creator profile not found");
      }

      console.log("[CreatorProfile] Successfully fetched profile data:", result[0]);
      
      return {
        ...result[0],
        socials: result[0].socials as Record<string, string> | null,
        show_powered_by: result[0].show_powered_by ?? true
      } as Profile;
    },
    enabled: !!username && username !== "student-auth",
    retry: 0,
    staleTime: 60 * 1000,
  });

  const { 
    data: courses = [],
    isLoading: coursesLoading
  } = useQuery({
    queryKey: ['creatorCourses', profile?.id],
    queryFn: async () => {
      if (!profile?.id) {
        console.warn("[CreatorProfile] No creator ID available to fetch courses");
        return [];
      }
      
      console.log("[CreatorProfile] Fetching courses for creator ID:", profile.id);
      
      const { data, error: supabaseError } = await supabase
        .from("courses")
        .select("*")
        .eq("creator_id", profile.id)
        .eq("published", true)
        .order("created_at", { ascending: false });

      if (supabaseError) {
        console.error("[CreatorProfile] Error fetching courses:", supabaseError);
        throw new Error(`Could not fetch courses: ${supabaseError.message}`);
      }

      console.log("[CreatorProfile] Successfully fetched creator courses:", data?.length || 0, "courses found");
      
      const coursesWithUpdates = data?.map(course => ({
        ...course,
        updated_at: course.created_at
      })) || [];
      
      return coursesWithUpdates;
    },
    enabled: !!profile?.id,
    retry: 0,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (profileError) {
      console.error("[CreatorProfile] Profile error:", profileError);
    }
  }, [profileError, navigate]);

  if (profileLoading) {
    return <ProfileLoading />;
  }

  if (profileError || !profile) {
    return <NotFound />;
  }

  return (
    <div className="min-h-screen bg-background">
      {profile ? (
        <>
          <ProfileHeader profile={profile} />
          
          <div className="container mx-auto px-4 relative">
            <div className="flex flex-col md:flex-row gap-6 -mt-16 mb-12">
              <div className="flex-shrink-0 flex flex-col items-center md:items-start">
                {/* Intentionally left empty to maintain layout */}
              </div>
              
              <SocialLinks profile={profile} />
            </div>

            <section className="my-12">
              <h2 className="text-2xl font-bold mb-6">Courses by {profile.full_name || profile.username}</h2>
              
              {coursesLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-64 bg-muted/30 animate-pulse rounded-lg"></div>
                  ))}
                </div>
              ) : (
                <CourseGrid 
                  courses={courses} 
                  username={profile.username || ""} 
                  creatorName={profile.full_name || profile.username}
                />
              )}
            </section>
          </div>

          <ProfileFooter profile={profile} />
        </>
      ) : (
        <ProfileError error="Creator profile could not be loaded" />
      )}
    </div>
  );
};

export default CreatorProfile;
